import {
  CheckCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  FieldTimeOutlined,
} from "@ant-design/icons";
import { Table, Tag, Space, Tooltip, Pagination, Icon, Popover } from "antd";
import React, { useEffect, useState } from "react";
import DeletePerfCycleApi from "../../api/DeleteCycleApi";
import {
  openNotificationError,
  openNotificationSuccess,
} from "../../helpers/NotificationHelpers";
import ConfirmationModal from "../modal/ConfirmationModal";
import EditPerfCycleModal from "../modal/edit/EditPerfCycleModal";
import moment from "moment";
import ActivePerfCycleApi from "../../api/ActivePerfCycleApi";
import HardDeleteModal from "../modal/HardDeleteModal";
import CompletePerfCycleApi from "../../api/CompletePerfCycleApi";
import { useSelector } from "react-redux";
import { permissionAbility } from "../../helpers/permissionHelpers";

function PerfCycleTable(props) {
  const [editModal, setEditModal] = useState(false);
  const [editCycle, setEditCycle] = useState();
  const [deleteModal, setDeleteModal] = useState(false);
  const [activeModal, setActiveModal] = useState(false);
  const [completeModal, setCompleteModal] = useState(false);
  const [deleteCycleId, setDeleteCycleId] = useState("");
  const [activeCycleId, setActiveCycleId] = useState("");
  const [completeCycleId, setCompleteCycleId] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const positionState = useSelector((state) => state.positionState.value);
  const userLevel = positionState?.userRole?.name;

  useEffect(() => {
    setLoading(props.loading);
  }, [props.cycles, props.loading]);

  /*If page changed from parent component */
  useEffect(() => {
    setPage(props.page);
  }, [props.page]);

  async function deleteCycle() {
    const apiResponse = await DeletePerfCycleApi(deleteCycleId);
    if (apiResponse.statusCode) {
      openNotificationError("bottomRight", apiResponse.message);
    } else if (apiResponse.message === "success") {
      openNotificationSuccess("bottomRight", "Cycle deleted");
      if (props.data?.performanceCycles?.length === 1) {
        props.changePage(page - 1);
      } else {
        props.changePage(page);
      }
    } else {
      openNotificationError("bottomRight", "Couldn't delete. Try again later.");
    }
    setDeleteCycleId("");
  }

  async function activateCycle() {
    const apiResponse = await ActivePerfCycleApi(
      activeCycleId,
      positionState?.id
    );
    if (apiResponse.statusCode) {
      openNotificationError("bottomRight", apiResponse.message);
    } else if (apiResponse.message === "success") {
      openNotificationSuccess("bottomRight", "Perf cycle activated");
      props.changePage(page);
    } else {
      openNotificationError(
        "bottomRight",
        "Couldn't activate. Try again later."
      );
    }
    setActiveCycleId("");
  }

  async function completeCycle() {
    const apiResponse = await CompletePerfCycleApi(
      completeCycleId,
      positionState?.id
    );
    if (apiResponse.statusCode) {
      openNotificationError("bottomRight", apiResponse.message);
    } else if (apiResponse.message === "success") {
      openNotificationSuccess("bottomRight", "Perf cycle completed");
      props.changePage(page);
    } else {
      openNotificationError(
        "bottomRight",
        "Couldn't complete. Try again later."
      );
    }
    setCompleteCycleId("");
  }

  function getColor(status) {
    if (status === "SCHEDULED") {
      return "orange";
    } else if (status === "Active") {
      return "green";
    } else if (status === "Completed") {
      return "blue";
    } else {
      return "red";
    }
  }

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => {
        const aName = a.name || "";
        const bName = b.name || "";
        return aName.localeCompare(bName);
      },
    },
    {
      title: "Starting on",
      dataIndex: "startingOn",
      key: "startingOn",
      sorter: (a, b) => {
        const aDate = new Date(a.startingOn);
        const bDate = new Date(b.startingOn);
        return aDate - bDate;
      },
      render: (text) => <div>{moment(text).format("YYYY-MM-DD")}</div>,
    },
    {
      title: "Ending on",
      dataIndex: "endingOn",
      key: "endingOn",
      sorter: (a, b) => {
        const aDate = new Date(a.endingOn);
        const bDate = new Date(b.endingOn);
        return aDate - bDate;
      },
      render: (text) => <div>{moment(text).format("YYYY-MM-DD")}</div>,
    },
    {
      title: "Template",
      dataIndex: "template",
      key: "template",
      sorter: (a, b) => {
        const aTemplate = a.templateName || "";
        const bTemplate = b.templateName || "";
        return aTemplate.localeCompare(bTemplate);
      },
      render: (text, record) => <div>{record.templateName}</div>,
    },
    {
      title: "Status",
      key: "status",
      dataIndex: "status",
      sorter: (a, b) => {
        const aStatus = a.status || "";
        const bStatus = b.status || "";
        return aStatus.localeCompare(bStatus);
      },
      filters: [
        { text: 'Active', value: 'Active' },
        { text: 'Scheduled', value: 'SCHEDULED' },
        { text: 'Completed', value: 'Completed' },
      ],
      onFilter: (value, record) => record.status === value,
      render: (text) => <Tag color={getColor(text)}>{text?.toUpperCase()}</Tag>,
    },
    {
      title: "Action",
      key: "action",
      render: (text, record) => (
        <Space size="middle">
          {permissionAbility(userLevel, "perf-cycles", "manage") &&
            (record.status == "Active" || record.status == "Completed" ? (
              record.status == "Completed" ? (
                <Tooltip title={"ReActive"}>
                  <div
                    onClick={() => {
                      setActiveModal(true);
                      setActiveCycleId(record.id);
                    }}
                    className="text-green-500 hover:text-green-600 cursor-pointer"
                  >
                    <CheckCircleOutlined />
                  </div>
                </Tooltip>
              ) : (
                <Tooltip title={"Complete"}>
                  <div
                    onClick={() => {
                      setCompleteModal(true);
                      setCompleteCycleId(record.id);
                    }}
                    className="text-orange-500 hover:text-orange-600 cursor-pointer"
                  >
                    <FieldTimeOutlined />
                  </div>
                </Tooltip>
              )
            ) : (
              <Tooltip title={"Active"}>
                <div
                  onClick={() => {
                    setActiveModal(true);
                    setActiveCycleId(record.id);
                  }}
                  className="text-green-500 hover:text-green-600 cursor-pointer"
                >
                  <CheckCircleOutlined />
                </div>
              </Tooltip>
            ))}
          {permissionAbility(userLevel, "perf-cycles", "edit") && (
            <Tooltip title={"Edit"}>
              <div
                onClick={() => {
                  setEditModal(true);
                  setEditCycle(record);
                }}
                className="text-blue-500 hover:text-blue-600 cursor-pointer"
              >
                <EditOutlined />
              </div>
            </Tooltip>
          )}
          {permissionAbility(userLevel, "perf-cycles", "delete") &&
            (record.canDelete ? (
              <Tooltip title={"Delete"}>
                <div
                  onClick={() => {
                    setDeleteModal(true);
                    setDeleteCycleId(record.id);
                  }}
                  className="text-red-500 hover:text-red-600 cursor-pointer"
                >
                  <DeleteOutlined />
                </div>
              </Tooltip>
            ) : (
              <Popover
                placement="left"
                content={
                  <div className="text-xs">
                    {record.status != "Scheduled"
                      ? "Can't delete active or completed cycles"
                      : "Can't delete cycles with plans"}
                  </div>
                }
                trigger="hover"
              >
                <div className="text-gray-500">
                  <DeleteOutlined />
                </div>
              </Popover>
            ))}
        </Space>
      ),
    },
  ];

  return (
    <>
      <Table
        columns={columns}
        dataSource={props.data?.performanceCycles}
        pagination={false}
        loading={loading}
      />
      <div className="text-center mt-6">
        <Pagination
          onChange={(num) => props.changePage(num)}
          current={page}
          total={props.data?.totalCount || 0}
          pageSize={props.data?.limit}
        />
      </div>
      <HardDeleteModal
        visible={deleteModal}
        handleCancel={() => setDeleteModal(false)}
        handleOk={() => deleteCycle()}
        text="Are you sure want to delete this cycle?"
        title="Delete perf cycle"
      />
      <ConfirmationModal
        visible={activeModal}
        handleCancel={() => setActiveModal(false)}
        handleOk={() => activateCycle()}
        text="Are you sure want to activate this cycle?"
        title="Activate perf cycle"
      />
      <ConfirmationModal
        visible={completeModal}
        handleCancel={() => setCompleteModal(false)}
        handleOk={() => completeCycle()}
        text="Are you sure want to complete this cycle?"
        title="Complete perf cycle"
      />
      <EditPerfCycleModal
        visible={editModal}
        handleCancel={() => {
          setEditModal(false);
          setEditCycle();
        }}
        handleOk={() => props.changePage(page)}
        cycle={editCycle}
      />
    </>
  );
}

export default PerfCycleTable;
